// hooks
import type { TFunction } from 'i18next';

import { useSelector } from 'hooks';
import useSubNavigation from 'hooks/useSubNavigation';
import { useTranslation } from 'react-i18next';

// components
import { Box } from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { TabPanel } from 'components/shared';
import AIMatchSettings from './AIMatch/AIMatch';
import Contacts from './Contacts/Contacts';
import DeployHistory from './DeployHistory/DeployHistory';
import KidsPlan from './KidsPlan/KidsPlan';
import RegionalPool from './RegionalPool/RegionalPool';
import DojoTabContent from './components/DojoTabContent';

// style
import { StyledStickyPaper } from 'components/shared/Paper/StyledStickyPaper';
import StyledTabs from 'components/shared/StyledTabs';

type SettingsGrants = {
  hasKidsPlan: boolean;
  hasRegionalPoolOrganisationAccess: boolean;
  hasAutoAskDutyRoundsViewAccess: boolean;
  hasDutyAutoAskEmployeesBasic: boolean;
};

function getSettingsTabs(t: TFunction, grants: SettingsGrants) {
  const {
    hasKidsPlan,
    hasRegionalPoolOrganisationAccess,
    hasAutoAskDutyRoundsViewAccess,
    hasDutyAutoAskEmployeesBasic
  } = grants;

  return [
    {
      id: DOJO_TAB.ADMIN,
      renderId: 'renderAdminSettingsAdminTab',
      destroyId: 'destroyAdminSettingsAdminTab',
      title: t('admin'),
      grant: true
    },
    {
      id: DOJO_TAB.ROSTER,
      renderId: 'renderAdminSettingsRosterTab',
      destroyId: 'destroyAdminSettingsRosterTab',
      title: t('roster'),
      grant: true
    },
    {
      id: DOJO_TAB.IMPORT,
      renderId: 'renderAdminSettingsImportTab',
      destroyId: 'destroyAdminSettingsImportTab',
      title: t('import'),
      grant: true
    },
    {
      id: 'KIDS_PLAN',
      title: t('adminPortal.settings.kidsPlan.tabTitle'),
      content: <KidsPlan />,
      grant: hasKidsPlan
    },
    {
      id: 'REGIONAL_POOL',
      title: t('regionalPool'),
      content: <RegionalPool />,
      grant: hasRegionalPoolOrganisationAccess
    },
    {
      id: 'CONTACTS',
      title: t('contacts'),
      content: <Contacts />,
      grant: true
    },
    {
      id: 'DEPLOY_HISTORY',
      title: t('deployHistory.title'),
      content: <DeployHistory />,
      grant: true
    },
    {
      id: 'AI_MATCH_SETTINGS',
      title: t('aiPlanning'),
      content: <AIMatchSettings />,
      grant: hasAutoAskDutyRoundsViewAccess || hasDutyAutoAskEmployeesBasic
    }
  ];
}

enum DOJO_TAB {
  ADMIN = 'ADMIN',
  IMPORT = 'IMPORT',
  ROSTER = 'ROSTER'
}

const Settings = () => {
  'use memo';

  const { t } = useTranslation('common');
  const { activePage: _activePage, setPage } = useSubNavigation<
    DOJO_TAB | string
  >(DOJO_TAB.ADMIN.toLowerCase());
  const {
    hasRegionalPoolOrganisationAccess,
    hasAutoAskDutyRoundsViewAccess,
    hasDutyAutoAskEmployeesBasic,
    hasKidsPlan
  } = useSelector((state) => state.grants);

  const onTabChanged = (_, newValue: DOJO_TAB | string) => {
    setPage(newValue.toLowerCase());
  };

  const activePage = _activePage.toUpperCase();

  const tabs = getSettingsTabs(t, {
    hasKidsPlan,
    hasRegionalPoolOrganisationAccess,
    hasAutoAskDutyRoundsViewAccess,
    hasDutyAutoAskEmployeesBasic
  });

  const tabsWithGrants = tabs.filter((tab) => tab.grant);

  return (
    <StyledTabs className="tabs">
      <StyledStickyPaper square>
        <Tabs
          value={activePage}
          indicatorColor="primary"
          textColor="primary"
          onChange={onTabChanged}
        >
          {tabsWithGrants.map((tab) => (
            <Tab key={tab.id} label={tab.title} value={tab.id} />
          ))}
        </Tabs>
      </StyledStickyPaper>

      <Box className="content">
        {tabsWithGrants.map((tab) => (
          <TabPanel
            key={tab.id}
            value={tab.id}
            activePage={activePage}
            title={tab.title}
          >
            {tab.renderId && tab.destroyId ? (
              <DojoTabContent
                renderId={tab.renderId}
                destroyId={tab.destroyId}
              />
            ) : (
              tab.content
            )}
          </TabPanel>
        ))}
      </Box>
    </StyledTabs>
  );
};

export default Settings;
